<script setup>
import { onBeforeMount, onUnmounted, ref } from 'vue'
import { API, HOST } from '@/utils/http'

const props = defineProps({
  code: {
    type: String,
    required: true
  }
})

// Capture happens in a background task right after the report syncs to the server,
// so a freshly created report may not have weather yet. Poll briefly until it lands,
// then settle to the "not available" message for reports that never got a snapshot.
const POLL_INTERVAL_MS = 5000
const POLL_WINDOW_MS = 60000

const loading = ref(true)
const weather = ref(null)

let pollTimer = null
let pollDeadline = 0

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
}

const schedulePoll = () => {
  if (Date.now() >= pollDeadline) {
    stopPolling()
    return
  }
  pollTimer = setTimeout(fetchWeather, POLL_INTERVAL_MS)
}

const fetchWeather = async () => {
  try {
    const { data } = await API.get(`${HOST}/geo/feature/${props.code}/weather`)
    weather.value = data
    if (data?.available) {
      stopPolling()
    } else {
      schedulePoll()
    }
  } catch (e) {
    // Don't poll on errors (403/404/auth/etc.) — these are non-transient and would
    // just spam failing requests. Polling only retries the "not captured yet" case.
    weather.value = { available: false }
    stopPolling()
  } finally {
    loading.value = false
  }
}

onBeforeMount(() => {
  pollDeadline = Date.now() + POLL_WINDOW_MS
  fetchWeather()
})

onUnmounted(stopPolling)
</script>

<template>
  <div class="bg-background mb-3">
    <template v-if="!loading">
      <div v-if="weather && weather.available">
        <div class="flex items-center gap-3 text-sm">
          <span class="text-lg">{{ weather.emoji }}</span>
          <span>{{ weather.label }}</span>
          <span v-if="weather.temperature != null" class="font-medium">{{ Math.round(weather.temperature) }}°C</span>
          <span v-if="weather.wind_speed != null" class="text-muted-foreground">💨 {{ Math.round(weather.wind_speed) }} km/h</span>
          <span v-if="weather.humidity != null" class="text-muted-foreground">💧 {{ Math.round(weather.humidity) }}%</span>
        </div>
        <p class="mt-1 text-xs text-muted-foreground">
          Captured when the report synced with the server.
        </p>
      </div>

      <div v-else class="text-sm text-muted-foreground">
        Weather unavailable.
      </div>
    </template>
  </div>
</template>
